import { useState, useEffect, useCallback } from "react";

interface Subscription {
  status: string | null;
  current_period_end: string | null;
}

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  isPro: boolean;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useSubscription(address?: string): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!address) {
        setSubscription(null);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/wallet-subscription-status?address=${encodeURIComponent(address)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Failed to fetch subscription status (${response.status})`);
      }

      const json = (await response.json()) as { subscription: Subscription | null };
      setSubscription(json.subscription);
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch subscription"));
    } finally {
      setIsLoading(false);
    }
  }, [address]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Compute isPro status
  const isPro = Boolean(
    subscription?.status === "active" &&
    subscription?.current_period_end &&
    new Date(subscription.current_period_end) > new Date()
  );

  return {
    subscription,
    isPro,
    isLoading,
    error,
    refresh: fetchSubscription,
  };
}
